// Names the user gives their BLE equipment ("C2 Rower", "Echo Bike"), shown in
// place of the name the device advertises ("PM5 431234"). Keyed by the same
// signature the roster uses to recognise gear across browsers (kind + type +
// advertised name), so the advertised name itself never changes. Kept in
// localStorage so it works signed out; with an account, the server copy wins
// (migration 048), which carries a name to the user's other phones.

import { deviceSignature } from "./equipmentRoster";

const KEY = "khronos_equipment_labels";

/** A view's kind/type for its signature: HR straps are "hrm" + "hr_monitor". */
export const gearIdentity = ({ kind, type, name }) => {
  const isHr = kind === "hrm" || kind === "hr" || type === "hr_monitor";
  return isHr
    ? { kind: "hrm", type: "hr_monitor", name }
    : { kind: "ftms", type, name };
};

export const labelKey = (gear) => deviceSignature(gearIdentity(gear));

export const readLabels = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeLabels = (labels) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(labels));
  } catch {
    /* storage blocked: names last for this visit only */
  }
};

/** Set (or clear, with an empty name) one gear's name. Returns the new map. */
export const setLocalLabel = (labels, gear, label) => {
  const next = { ...labels };
  const clean = String(label || "").replace(/\s+/g, " ").trim().slice(0, 60);
  if (clean) next[labelKey(gear)] = clean;
  else delete next[labelKey(gear)];
  writeLabels(next);
  return next;
};

/**
 * Fold the account roster in: for any gear the server knows about, its label
 * (or the lack of one) wins. Gear only known here keeps its local name.
 */
export const mergeServerLabels = (labels, rows = []) => {
  const server = new Map();
  for (const row of rows) {
    if (!row?.name || !row?.kind) continue;
    const key = labelKey({ kind: row.kind, type: row.equipment_type, name: row.name });
    // Every row for one gear carries the same label; any that has one wins.
    if (row.label || !server.has(key)) server.set(key, row.label || server.get(key) || null);
  }
  const next = { ...labels };
  for (const [key, label] of server) {
    if (label) next[key] = label;
    else delete next[key];
  }
  writeLabels(next);
  return next;
};

/** The name to show: the user's, else the advertised one. */
export const displayName = (labels, gear) => labels[labelKey(gear)] || gear.name;
